import React from 'react';
import {get, map, sortBy} from 'lodash';
import DialogComponent from 'components/Dialog';
import {formatDateTime} from 'common/dateUtils';
import {DialogTypes} from 'common/names';
import {getOsUserString} from 'common/stringUtils';
import Button from 'components/Button';
import Panel from 'components/Panel';
import {useConnections} from 'features/login/redux/hooks/useConnections';
import {useLogoff} from 'features/login/redux/hooks/useLogoff';
import i18n from 'i18n';
import {Trans} from 'react-i18next';
import {useSelector} from 'react-redux';
import {licenseSelectors} from 'redux/license';
import {useServiceProperties} from 'redux/server/hooks/useServiceProperties';
import {DiplomatFeature} from 'types/DiplomatFeature';
import {useServerLogins} from 'redux/server/hooks/useServerLogins';
import {Grid} from '@mui/material';
import {
   GridColDef,
   DataGridPro,
   GridValueGetterParams,
   GridRowParams,
   GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import {enums} from 'types/enums';
import {DiplomatServerState} from 'types/DiplomatServiceProperties';
import {loginSelectors} from 'features/login/redux';
import {buildNumber, versionNumber} from 'common/constants';

const cols: GridColDef[] = [
   {
      field: 'user.contactName',
      headerName: i18n.t('Name'),
      flex: 140,
      valueGetter: (params: GridValueGetterParams) => get(params.row, 'user.contactName'),
   },
   {
      field: 'component',
      headerName: i18n.t('Connection'),
      flex: 120,
      valueGetter: (params: GridValueGetterParams) =>
         enums.component[params.row?.component as keyof typeof enums.component],
   },
   // {
   //    field: 'connectionType',
   //    headerName: i18n.t('Access Type'),
   //    flex: 120,
   // },
   // {
   //    field: 'authenticationStrategy',
   //    headerName: i18n.t('Authentication'),
   //    flex: 130,
   // },
   {
      field: 'user.role',
      headerName: i18n.t('Access'),
      flex: 150,
      valueGetter: (params: GridValueGetterParams) =>
         enums.role[get(params.row, 'user.role') as keyof typeof enums.role.enum],
   },
   {
      field: 'ipaddress',
      headerName: i18n.t('IP Address'),
      flex: 130,
   },
   // {
   //    field: 'protocol',
   //    headerName: i18n.t('Protocol'),
   //    flex: 100,
   //    // valueGetter: (params: ValueGetterParams) =>
   //    //    enums.role[get(params.data, 'protocol') as keyof typeof enums.role.enum],
   // },
   {
      field: 'connectionTime',
      headerName: i18n.t('Connected Since'),
      flex: 130,
      valueFormatter: (params: GridValueFormatterParams) => formatDateTime(params.value as Date),
   },
   {
      field: 'lastActivity',
      headerName: i18n.t('Last Activity'),
      flex: 130,
      valueFormatter: (params: GridValueFormatterParams) => formatDateTime(params.value as Date),
   },
];

type ConnectionsPanelProps = {
   logins: boolean;
   disabledBy: string;
   refreshServiceProps: () => void;
};
const ConnectionsPanel = (props: ConnectionsPanelProps) => {
   const {logins, disabledBy, refreshServiceProps} = props;

   const [selected, _setSelected] = React.useState<string[]>([]);
   const [connections, refreshConnections] = useConnections();
   const currentUser = useSelector(loginSelectors.userName);
   const postLogoffSelected = useLogoff({
      onSuccess: refreshConnections,
   });
   const postServerLogins = useServerLogins(!logins, {
      onSuccess: () => refreshServiceProps(),
   });

   const enableDisableButton = logins ? i18n.t('Disable Logins') : i18n.t('Enable Logins');
   const enableDisableLabel = logins
      ? i18n.t('Logins are enabled.')
      : `${i18n.t('Logins have been disabled by')} ${disabledBy}.`;

   const rows = React.useMemo(
      () =>
         sortBy(
            map(connections, (c) => ({
               id: c.diplomatConnection.connectionID,
               ...c.diplomatConnection,
               anotherConnection: !c.currentConnection,
               lastActivity: c.lastActivity,
            })),
            ['anotherConnection', 'id']
         ),
      [connections]
   );

   const setSelected = React.useCallback(
      (ids: string[]) => {
         // const currentUserRow = rows.find((r) => r.user.userName === currentUser);
         // let ids = _ids;
         // if (currentUserRow) {
         //    ids = without(ids, currentUserRow.id);
         // }
         _setSelected(ids);
      },
      [currentUser, rows]
   );

   return (
      <Panel name={i18n.t('Connections')}>
         <Grid container spacing={3}>
            <Grid item style={{height: 200, width: '100%'}}>
               <DataGridPro
                  rows={rows}
                  columns={cols}
                  density="compact"
                  disableExtendRowFullWidth={false}
                  pageSize={3}
                  checkboxSelection
                  onSelectionModelChange={(selectionModel) => {
                     setSelected(selectionModel as string[]);
                  }}
                  isRowSelectable={(params: GridRowParams) => params.row?.anotherConnection}
               />
            </Grid>
         </Grid>
         <Grid container spacing={1}>
            <Grid item container spacing={3}>
               <Grid item>
                  <Trans>
                     * Your current Diplomat MFT Client session cannot be selected for logoff.
                  </Trans>
               </Grid>
            </Grid>
            <Grid item container spacing={3}>
               {/* <Grid item>
               <Button>
                  <Trans>Select All</Trans>
               </Button>
            </Grid>
            <Grid item>
               <Button>
                  <Trans>Unselect All</Trans>
               </Button>
            </Grid> */}
               <Grid item>
                  <Button
                     onClick={() => postLogoffSelected({connections: selected})}
                     disabled={!selected.length}
                  >
                     <Trans>Logoff Selected</Trans>
                  </Button>
               </Grid>
            </Grid>
            <Grid item container spacing={3} alignItems="center">
               <Grid item>
                  <Button onClick={() => postServerLogins()}>{enableDisableButton}</Button>
               </Grid>
               <Grid item>
                  <div>{enableDisableLabel}</div>
               </Grid>
            </Grid>
            <Grid item container spacing={3}>
               <Grid item>
                  <Button onClick={refreshConnections}>
                     <Trans>Refresh</Trans>
                  </Button>
               </Grid>
            </Grid>
         </Grid>
      </Panel>
   );
};

type Props = {
   onClose: () => void;
};
const SystemStatusDialog = React.memo((props: Props) => {
   const {onClose} = props;

   const isFeatureSupported = useSelector(licenseSelectors.isFeatureSupported);
   const [serviceProps, refreshServiceProps] = useServiceProperties();

   return (
      <DialogComponent
         maxWidth="lg"
         header={DialogTypes.SystemStatusDialog.dialogName}
         close={onClose}
      >
         <Grid container spacing={3}>
            <Grid item sm={12} lg={6}>
               <div>
                  <Panel name={i18n.t('Diplomat MFT Service')}>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Service State:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{DiplomatServerState[serviceProps.serverState]}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Service Start Time:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{formatDateTime(serviceProps.serverStartTime)}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Service Logon Account:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{getOsUserString(serviceProps.serverSession.user)}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Service Max Memory (GB):</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>
                              {Math.round((serviceProps.maxMemory / (1024 * 1024 * 1024)) * 100) /
                                 100}
                           </div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Version:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>Version {versionNumber}</div>
                           {/* <div>{serviceProps.serverVersion}</div> */}
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Diplomat Build Number:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.serverBuildId}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>JRE Version:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.jreVersion}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Tomcat Version:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.tomcatVersion}</div>
                        </Grid>
                     </Grid>
                  </Panel>
               </div>
            </Grid>
            <Grid item sm={12} lg={6}>
               <div>
                  <Panel name={i18n.t('System')}>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Host Name:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.serverDisplayName}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>IP Address:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps?.serverSession?.ipaddress}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Operating System:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.osName}</div>
                        </Grid>
                     </Grid>
                     <Grid container spacing={3}>
                        <Grid item xs={12} lg={6}>
                           <div>
                              <Trans>Architecture:</Trans>
                           </div>
                        </Grid>
                        <Grid item xs={12} lg={6}>
                           <div>{serviceProps.osArchitecture}</div>
                        </Grid>
                     </Grid>
                  </Panel>
               </div>
            </Grid>
         </Grid>
         {isFeatureSupported(DiplomatFeature.USER_ADMIN) ? (
            <ConnectionsPanel
               logins={serviceProps.logins}
               disabledBy={serviceProps?.loginsDisabler?.contactName}
               refreshServiceProps={refreshServiceProps}
            />
         ) : null}
      </DialogComponent>
   );
});

export default SystemStatusDialog;
